import React, { useCallback, useState } from "react"
import styled, { css } from "styled-components"
import {
  DesktopOnlyDiv,
  Flex,
  SPOnlyDiv,
  StyledLink,
} from "../../styles/base-styles"
import { WpPost } from "../../types/wordpress-types"
import { getCategorySettings, TEXTS } from "../../utils/constants"
import { CategoryThumbnail } from "../common/category-image"
import { PostLinkIcon } from "../../images/images"
import { Icons } from "../icons/icons"

interface Props {
  posts: WpPost[]
  isTable?: boolean
}
interface PostLinkProps {
  post: WpPost
  isTable?: boolean
}
const getActualPost = (post: WpPost) => ({
  title: post.title,
  id: post.id,
  databaseId: post.databaseId,
  category: post.categories?.nodes[0],
  categoryColor: getCategorySettings(post.categories?.nodes[0]?.databaseId)
    .color,
  date: post.date,
  featureImage: post.featuredImage?.node.sourceUrl,
  gatsbyImage: post.featuredImage?.node.gatsbyImage
})

export const PostLinks: React.FC<Props> = ({ posts, isTable }) => {
  const SPPostLink: React.FC<PostLinkProps> = ({ post, isTable }) => {
    const actualPost = getActualPost(post)
    const [isMouseEntered, setIsMouseEntered] = useState(false)
    const handleMouseEnter = useCallback(() => {
      setIsMouseEntered(true)
    }, [])

    const handleMouseLeave = useCallback(() => {
      setIsMouseEntered(false)
    }, [])
    return (
      <RelatedPost
        as={StyledLink}
        to={`/${actualPost.databaseId}`}
        key={actualPost.databaseId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <PostInfoDiv>
          <RelatedPostTitle isTable={isTable}>
            {actualPost.title}
          </RelatedPostTitle>
          <ThumbnailDivSP>
            <CategoryThumbnail
              className="post_thumbnail_image"
              width={103}
              height={54}
              mobileWidth={80}
              mobileHeight={42}
              categoryDatabaseId={actualPost.category?.databaseId}
              featureImageUrl={actualPost.featureImage}
              gatsbyImage={actualPost.gatsbyImage}
            />
          </ThumbnailDivSP>
        </PostInfoDiv>
        <PostLinkIconDiv>
          <p>記事へ</p>
          {isTable ? (
            <Icons.CategoryChevron
              width={12}
              changeColorOnActive
              isActive={isMouseEntered}
              activeColor="#ef7a00"
            />
          ) : (
            <PostLinkIcon changeColorOnActive activeColor="#ef7a00" />
          )}
        </PostLinkIconDiv>
      </RelatedPost>
    )
  }
  const DesktopPostLink: React.FC<PostLinkProps> = ({ post }) => {
    const actualPost = getActualPost(post)
    const [isMouseEntered, setIsMouseEntered] = useState(false)
    const handleMouseEnter = useCallback(() => {
      setIsMouseEntered(true)
    }, [])

    const handleMouseLeave = useCallback(() => {
      setIsMouseEntered(false)
    }, [])
    return (
      <RelatedPostDesktop
        as={StyledLink}
        to={`/${actualPost.databaseId}`}
        key={actualPost.databaseId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <PostInfoDivDesktop>
          <RelatedPostTitle>{actualPost.title}</RelatedPostTitle>
          <PostLinkIconDiv>
            <p>記事へ</p>
            <Icons.CategoryChevron
              width={12}
              changeColorOnActive
              isActive={isMouseEntered}
              activeColor="#ef7a00"
            />
          </PostLinkIconDiv>
        </PostInfoDivDesktop>
        <ThumbnailDivDesktop>
          <CategoryThumbnail
            className="post_thumbnail_image"
            width={180}
            height={94}
            categoryDatabaseId={actualPost.category?.databaseId}
            featureImageUrl={actualPost.featureImage}
            gatsbyImage={actualPost.gatsbyImage}
          />
        </ThumbnailDivDesktop>
      </RelatedPostDesktop>
    )
  }

  return (
    <PostLinksDiv isTable={isTable}>
      {posts.map((post, i) => (
        <React.Fragment key={`postlink#${i}`}>
          {/* SP */}
          <SPOnlyDiv>
            <SPPostLink post={post} />
          </SPOnlyDiv>
          {/* Desktop */}
          <DesktopOnlyDiv>
            {isTable ? (
              <SPPostLink post={post} isTable />
            ) : (
              <DesktopPostLink post={post} />
            )}
          </DesktopOnlyDiv>
        </React.Fragment>
      ))}
    </PostLinksDiv>
  )
}

const PostLinksDiv = styled.div<{ isTable?: boolean }>`
  display: grid;
  width: calc(100% - 64px);
  padding: 32px;
  grid-template-columns: 1fr;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-bottom: 64px;
  background: var(--White_Gray, #f4f4f4);

  ${({ isTable }) =>
    isTable &&
    css`
      gap: 8px;
      grid-template-columns: 1fr 1fr;
      /* SP style */
      @media screen and (max-width: 767px) {
        gap: 6px;
        grid-template-columns: 1fr;
      }
    `}
  /* SP style */
  @media screen and (max-width: 767px) {
    width: calc(100% - 32px);
    padding: 16px;
    margin-bottom: 48px;
  }
`

const RelatedPost = styled.div`
  position: relative;
  height: 128px;
  background: white;
  padding: 16px 16px 14px 16px;
  display: grid;
  gap: 4px;
  color: var(--Dark_Gray, #242424);
  transition: 0.1s;
  .post_thumbnail_image {
    transition: transform 0.5s;
  }

  &:hover {
    color: #ef7a00 !important;
    .post_thumbnail_image {
      transform: scale(1.15);
      transition: transform 0.5s;
    }
  }
`
const RelatedPostDesktop = styled.div`
  position: relative;
  background: white;
  height: 94px;
  padding: 16px;
  display: flex;
  gap: 16px;
  color: var(--Dark_Gray, #242424);
  transition: 0.1s;
  .post_thumbnail_image {
    transition: transform 0.5s;
  }

  &:hover {
    color: #ef7a00 !important;
    .post_thumbnail_image {
      transform: scale(1.1);
      transition: transform 0.5s;
    }
  }
`

const PostInfoDiv = styled.div`
  display: flex;
  gap: 16px;
`
const PostInfoDivDesktop = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1 1 auto;
`
const PostLinkIconDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  p {
    text-align: right;
    font-family: san-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: bold;
    line-height: 16px; /* 145.455% */
  }
`
const ThumbnailDiv = styled.div`
  p {
    text-align: right;
    font-family: san-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: bold;
    line-height: 16px; /* 145.455% */
  }
`
const ThumbnailDivSP = styled(ThumbnailDiv)`
  flex: 0 0 80px;
`
const ThumbnailDivDesktop = styled(ThumbnailDiv)`
  flex: 0 0 180px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const RelatedPostTitle = styled.div<{ isTable?: boolean }>`
  font-feature-settings: "clig" off, "liga" off;
  font-family: san-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: bold;
  line-height: 28px; /* 175% */
  letter-spacing: 1.28px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  word-break: break-all;

  flex: 1 1 auto;
  ${({ isTable }) =>
    isTable &&
    css`
      -webkit-line-clamp: 4;
    `}
  /* SP style */
  @media screen and (max-width: 767px) {
    -webkit-line-clamp: 4;
    font-size: 14px;
    line-height: 25.2px; /* 25.2px */
  }
`

const CategoryLabel = styled.p<{ color?: string }>`
  position: absolute;
  height: 29px;
  border: 1px solid white;
  top: 11px;
  left: 11px;
  background: ${({ color }) => color || "black"};
  color: white;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 78px;
  padding: 0 12px;
  border-radius: 1px;
`
